import {
  getImagePreloadCandidates,
  getImagePreloadCandidatesForSources,
  getImageRenderMeta
} from "../components/imageMeta";
import { getPersonaHeroImages } from "../funnel/flowData";
import type { FunnelOption, FunnelStep } from "../funnel/types";

type BuildFunnelImageRepositoryInput = {
  steps: FunnelStep[];
  currentIndex: number;
  lookaheadSteps?: number;
};

export type FunnelImageRepository = {
  critical: string[];
  secondary: string[];
  all: string[];
  warnings: string[];
};

const DEFAULT_LOOKAHEAD_STEPS = 3;

const unique = (urls: Array<string | undefined | null>): string[] => {
  return Array.from(new Set(urls.filter((url): url is string => Boolean(url))));
};

const expandResponsiveUrls = (src?: string): string[] => {
  return getImagePreloadCandidates(src);
};

const optionImageUrls = (options: FunnelOption[]): string[] => {
  return options.flatMap((option) => expandResponsiveUrls(option.image));
};

const stepImageUrls = (step: FunnelStep): string[] => {
  switch (step.kind) {
    case "welcome":
      return [
        ...getImagePreloadCandidatesForSources(step.heroSlides?.map((slide) => slide.image)),
        ...expandResponsiveUrls(step.heroImage),
        ...(step.embeddedQuestion ? optionImageUrls(step.embeddedQuestion.options) : [])
      ];
    case "question":
      return [
        ...expandResponsiveUrls(step.mediaImage),
        ...optionImageUrls(step.options)
      ];
    case "affirmation":
      return expandResponsiveUrls(step.heroImage);
    case "loading":
      return expandResponsiveUrls(step.heroImage);
    case "result":
      return [...expandResponsiveUrls(step.heroImage), step.heroImageMask].filter(
        (url): url is string => Boolean(url)
      );
    case "email":
    case "payment":
      return [];
  }
};

const validationImageUrlsFromStep = (step: FunnelStep): string[] => {
  switch (step.kind) {
    case "welcome":
      return unique([
        ...(step.heroSlides?.map((slide) => slide.image) ?? []),
        step.heroImage,
        ...(step.embeddedQuestion?.options.map((option) => option.image) ?? [])
      ]);
    case "question":
      return unique([step.mediaImage, ...step.options.map((option) => option.image)]);
    case "affirmation":
      return unique([step.heroImage]);
    case "loading":
      return unique([step.heroImage]);
    case "result":
      return unique([step.heroImage]);
    case "email":
    case "payment":
      return [];
  }
};

const validateRootRelativeUrls = (urls: string[], warnings: string[]): void => {
  for (const url of urls) {
    if (!url.startsWith("/")) {
      warnings.push(`Image URL is not root-relative: ${url}`);
    }
  }
};

const validateImageMetaCoverage = (urls: string[], warnings: string[]): void => {
  for (const url of urls) {
    if (!getImageRenderMeta(url)) {
      warnings.push(`Image metadata is missing for: ${url}`);
    }
  }
};

export const validateFunnelImageRepository = (steps: FunnelStep[]): string[] => {
  const warnings: string[] = [];
  const stepUrls = unique(steps.flatMap(validationImageUrlsFromStep));
  const personaUrls = unique(getPersonaHeroImages());
  const renderImageUrls = unique([...stepUrls, ...personaUrls]);

  validateRootRelativeUrls(renderImageUrls, warnings);
  validateImageMetaCoverage(renderImageUrls, warnings);

  return unique(warnings);
};

export const buildFunnelImageRepository = ({
  steps,
  currentIndex,
  lookaheadSteps = DEFAULT_LOOKAHEAD_STEPS
}: BuildFunnelImageRepositoryInput): FunnelImageRepository => {
  const safeCurrentIndex =
    steps.length === 0 ? 0 : Math.min(Math.max(currentIndex, 0), steps.length - 1);
  const criticalEnd = Math.min(steps.length, safeCurrentIndex + lookaheadSteps + 1);
  const criticalSteps = steps.slice(safeCurrentIndex, criticalEnd);

  const critical = unique(criticalSteps.flatMap(stepImageUrls));
  const stepUrls = unique(steps.flatMap(stepImageUrls));
  const personaUrls = unique(getPersonaHeroImages().flatMap(expandResponsiveUrls));
  const allStepAndPersonaUrls = unique([...stepUrls, ...personaUrls]);
  const secondary = allStepAndPersonaUrls.filter((url) => !critical.includes(url));

  return {
    critical,
    secondary,
    all: [...critical, ...secondary],
    warnings: validateFunnelImageRepository(steps)
  };
};
